import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Simple hook to preserve a param in url search params, as a simple piece of state
 * It can replace a useState easily for simple values that need to be preserved in the url (eg selected tab in a tabbed view)
 */
export function useStateInUrl(paramName, defaultValue, type?) {
  const location = useLocation()
  const navigate = useNavigate()
  const [dirty, setDirty] = useState(false)
  const [value, setValue] = useState(defaultValue)

  const decodeValue = useCallback(
    (value?: string | null) => {
      if (type === Array) {
        return value?.split(',')
      } else if (type === Object) {
        return value ? JSON.parse(decodeURIComponent(value)) : value
      }
      return value
    },
    [type]
  )

  const encodeValue = useCallback(
    (value?: unknown): string => {
      if (type === Object) {
        return encodeURIComponent(JSON.stringify(value))
      }
      return value as string
    },
    [type]
  )

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const _defaultValue = dirty ? '' : defaultValue
    const parsedValue = decodeValue(searchParams.get(paramName))
    let fallbackValue = _defaultValue
    if (type === Array) {
      fallbackValue = _defaultValue || []
    }
    setValue(parsedValue || fallbackValue)
  }, [dirty, location.search, paramName, defaultValue, type, decodeValue])

  const setStateInUrl = (newValue) => {
    setDirty(true)
    // preserve other search params
    const existingSearch = location.search.replace(/^\?/, '') // Remove leading '?'
    const updatedSearchParams = new URLSearchParams(existingSearch)

    if (newValue === '' || (type === Array && !newValue?.length)) {
      updatedSearchParams.delete(paramName)
    } else {
      updatedSearchParams.set(paramName, encodeValue(newValue))
    }

    const newSearch = updatedSearchParams.toString()
    navigate({
      search: `?${newSearch}`
    })
  }

  return [value, setStateInUrl]
}
